import { Store, createEvent, createStore } from 'effector';
import { effectorStartEvent } from '@nx-frontend/shared/store/client-side';
import {
  portalThemeColors,
  applyThemeVariants,
  portalThemePresetByKey,
  portalThemeDefaultConfig,
} from '@nx-frontend/shared/themes';
import { themeColorsToHex } from '@nx-frontend/shared/themes/utils/themeColorsToHex';
import {
  PortalStyleConfig,
  PortalHeaderConfig,
  PortalFooterConfig,
  PortalSearchConfig,
  PortalColorSchemas,
  PortalSidebarConfig,
  PortalContentConfig,
  PortalSecondbarConfig,
  ThemeColorSchemas,
  PortalBreadcrumbsConfig,
  PortalThemeConfigPartial,
  PortalHeroConfig,
  PortalThemeConfig,
  ThemeHexColorSchemas,
} from '@nx-frontend/shared/types';
import { DeepPartial } from '@nx-frontend/shared/types';
import { patchObject, patchObjectMulti } from '@nx-frontend/shared/utils';

interface ThemeStoreState {
  path: string;
  theme: PortalThemeConfigPartial;
}

export const initialThemeState: ThemeStoreState = {
  path: '/',
  theme: {},
};

export const updateThemeEvent = createEvent<PortalThemeConfigPartial>();

export const $theme: Store<ThemeStoreState> = createStore(initialThemeState)
  .on(effectorStartEvent, (state) => state)
  .on(updateThemeEvent, (oldState, newTheme) => {
    if (newTheme.colors) {
      Object.values(PortalColorSchemas).forEach((scheme) => {
        if (
          newTheme.colors?.[scheme] &&
          oldState.theme.colors?.[scheme]?.key !== oldState.theme.colors?.[scheme]?.key
        ) {
          const { key, logo, icon, imagesBg, imagesHead } = oldState.theme.colors?.[scheme] || {};
          newTheme.colors[scheme] = {
            key,
            ...(logo ? { logo } : null),
            ...(icon ? { icon } : null),
            ...(imagesBg ? { imagesBg } : null),
            ...(imagesHead ? { imagesHead } : null),
          };
        }
      });
    }
    return { ...oldState, theme: newTheme };
  });

const getPreset = (colors?: DeepPartial<PortalThemeConfig['colors']>) => {
  return colors?.light?.key ? portalThemePresetByKey(colors.light.key) : null;
};

export const $themeHeader: Store<PortalHeaderConfig> = $theme.map(({ theme: { header, colors } }) =>
  patchObjectMulti<PortalHeaderConfig>(
    portalThemeDefaultConfig.header,
    [getPreset(colors)?.header ?? {}, header ?? {}],
    true
  )
);

export const $themeHero: Store<PortalHeroConfig> = $theme.map(({ theme: { hero, colors } }) =>
  patchObjectMulti<PortalHeroConfig>(portalThemeDefaultConfig.hero, [getPreset(colors)?.hero ?? {}, hero ?? {}], true)
);

export const $themeFooter: Store<PortalFooterConfig> = $theme.map(({ theme: { footer, colors } }) => ({
  ...portalThemeDefaultConfig.footer,
  ...getPreset(colors)?.footer,
  ...footer,
}));

export const $themeSidebar: Store<PortalSidebarConfig> = $theme.map(({ theme: { sidebar, colors } }) => ({
  ...portalThemeDefaultConfig.sidebar,
  ...getPreset(colors)?.sidebar,
  ...sidebar,
}));

export const $themeSecondbar: Store<PortalSecondbarConfig> = $theme.map(({ theme: { secondbar, colors } }) => ({
  ...portalThemeDefaultConfig.secondbar,
  ...getPreset(colors)?.secondbar,
  ...secondbar,
}));

export const $themeSearch: Store<PortalSearchConfig> = $theme.map(({ theme: { search, colors } }) => ({
  ...portalThemeDefaultConfig.search,
  ...getPreset(colors)?.search,
  ...search,
}));

export const $themeBreadcrumbs: Store<PortalBreadcrumbsConfig> = $theme.map(({ theme: { breadcrumbs, colors } }) => ({
  ...portalThemeDefaultConfig.breadcrumbs,
  ...getPreset(colors)?.breadcrumbs,
  ...breadcrumbs,
}));

export const $themeStyle: Store<PortalStyleConfig> = $theme.map(({ theme: { style, colors } }) =>
  patchObjectMulti<PortalStyleConfig>(
    portalThemeDefaultConfig.style,
    [getPreset(colors)?.style ?? {}, style ?? {}],
    true
  )
);

export const $themeContent: Store<PortalContentConfig> = $theme.map(({ theme: { content = {} } }) =>
  patchObject(portalThemeDefaultConfig.content, content, true)
);

export const $themeColors: Store<ThemeColorSchemas> = $theme.map(({ path, theme: { colors: customColors = {} } }) => {
  const colors = patchObject(portalThemeDefaultConfig.colors, customColors, true);
  return Object.values(PortalColorSchemas).reduce<ThemeColorSchemas>((colorSchemas, schema) => {
    if (schema === 'dark') {
      // Dark theme is temporarily disabled
      return colorSchemas;
    }
    const schemaColors = colors[schema];
    const baseColors = portalThemeColors.find((item) => item.key === schemaColors.key) || portalThemeColors[0];
    colorSchemas.push({
      schema,
      colors: applyThemeVariants({ path, theme: { ...baseColors, ...schemaColors } }),
    });
    return colorSchemas;
  }, []);
});

export const $themeIcon = $themeColors.map(([{ colors }]) => colors.icon);
export const $themeName = $themeColors.map(([{ colors }]) => colors.name);
export const $themeDesc = $themeColors.map(([{ colors }]) => colors.description);
export const $thumbnail = $themeColors.map(([{ colors }]) => colors.thumbnail);
export const $themeLogo = $themeColors.map(([{ colors }]) => colors.logo);
export const $themeBgImages = $themeColors.map(([{ colors }]) => colors.imagesBg);
export const $themeHeadImages = $themeColors.map(([{ colors }]) => colors.imagesHead);
export const $themeIconSet = $themeColors.map(([{ colors }]) => colors.iconSet);
export const $themeEditorColor = $themeColors.map(([{ colors }]) => colors.editorColor);
export const $themeHexColors: Store<ThemeHexColorSchemas> = $themeColors.map((themeColors) =>
  themeColorsToHex(themeColors)
);
